import _ from 'underscore';
import React, {
  forwardRef, useContext, useEffect, useRef, useState, useCallback, memo,
  type Ref,
} from 'react';
import { Activity, DirectLine, Message } from 'botframework-directlinejs';

import { IConversationThreadProps } from '../../types';
import { DirectLineContext } from '../direct-line-provider/direct-line-context';
import { IFeedbackData } from '../../FeedbackButtons';
import { BotActivity } from '../../botTypes';
import { useLoading } from '../../hooks/useLoading';
import { getOAuthCardResourceUri } from '../../hooks/useSSO';
import { ConversationThreadInternal } from './conversation-thread-internal';
import { getActivitiesWithFeedback } from './get-activities-with-feedback';
import { findExistingActivityIndex } from './find-existing-activity-index';
import { isTroubleshootingEvent } from '../../utils';

const ConversationThreadWithForwardRef = (props: IConversationThreadProps, ref: Ref<HTMLDivElement>) => {
  const { directLine, userid, username } = useContext(DirectLineContext)!;
  const {
    onReceiveActivity: originalOnReceiveActivity,
    onReceiveActivityEnd,
    onReceiveActivityStart,
    onFeedbackButtonClick,
    renderBottomComponent: originalRenderBottomComponent,
    isCustomStream,
    isStopGeneratingEnabled = false,
    isTroubleshootingEnabled,
  } = props;
  const refreshId = useRef(-1);
  const activitiesRef = useRef<Activity[]>([]);
  const existingActivitiesRef = useRef<Activity[]>([]);
  const [, forceUpdate] = useState(-1);
  const activitySubscriber = useRef<ReturnType<DirectLine['activity$']['subscribe']> | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>('');

  const sendFeedback = useCallback(({ prevFeedback, currentFeedback }: IFeedbackData, message: Message) => {
    if (!directLine) {
      return;
    }

    const reactionsRemoved = prevFeedback !== 'None' ? [{ type: prevFeedback }] : [];
    const reactionsAdded = currentFeedback !== 'None' ? [{ type: currentFeedback }] : [];
    const messageReactionAction = {
      type: 'messageReaction',
      replyToId: message.id,
      from: {
        id: userid,
        name: username,
      },
      reactionsRemoved,
      reactionsAdded,
    };

    directLine.postActivity((messageReactionAction as any)).subscribe(() => {
      if (onFeedbackButtonClick) {
        onFeedbackButtonClick({ prevFeedback, currentFeedback }, message);
      }
    });
  }, [directLine, userid, username, onFeedbackButtonClick]);

  const { onReceiveActivity, renderBottomComponent } = useLoading({
    renderBottomComponent: originalRenderBottomComponent,
    isCustomStream,
    isStopGeneratingEnabled,
    isStopTroubleshootingEnabled: isTroubleshootingEnabled,
    onReceiveActivity: originalOnReceiveActivity,
  });

  useEffect(() => {
    if (activitySubscriber.current) {
      activitySubscriber.current.unsubscribe();
    }

    if (directLine) {
      const subscribeTime = new Date();
      activitySubscriber.current = directLine.activity$.subscribe((activity: Activity) => {
        // Check if the activity ID exists in the DirectLine state.
        // When a live agent is active, messages sent won't have an activity ID,
        // so such messages are considered new.
        if (activity.id) {
          for (let i = existingActivitiesRef.current.length - 1; i >= 0; i -= 1) {
            if (existingActivitiesRef.current[i].id === activity.id) {
              // activity is already in the list, nothing to do
              return;
            }
          }
        }

        const convId = activity.conversation?.id;
        setConversationId(convId);

        const timestampDate = activity.timestamp ? new Date(activity.timestamp) : new Date();

        // if timestamp is before the subscription time, the activity is part of a restored conversation and not a new activity
        _.extend(activity, {
          isFromRestore: (activity as any).isFromRestore || timestampDate < subscribeTime,
          timestamp: timestampDate.getTime().toString(),
        });

        let finalActivities = activitiesRef.current;

        onReceiveActivityStart?.(activity, finalActivities);

        const messageActivity = activity as BotActivity;

        existingActivitiesRef.current = existingActivitiesRef.current.concat(activity);

        // 1. it is a message in streaming status
        // 2. it is a static message
        // 3. it is a troubleshooting event message
        if (messageActivity.value?.StreamingStatus
          || (messageActivity as Message)?.text === 'StaticMessages'
          || isTroubleshootingEvent(messageActivity)) {
          const existingActivityIndex = findExistingActivityIndex(finalActivities, activity, username);

          if (existingActivityIndex !== -1) {
            finalActivities[existingActivityIndex] = activity;
          } else {
            finalActivities = finalActivities.concat(activity);
          }
        } else {
          finalActivities = finalActivities.concat(activity);
        }

        if (onReceiveActivity) {
          finalActivities = onReceiveActivity(activity, finalActivities, directLine);
        }

        if (getOAuthCardResourceUri(activity)) {
          finalActivities.splice(-1);
        }

        activitiesRef.current = getActivitiesWithFeedback(activity, finalActivities);

        refreshId.current += 1;
        forceUpdate(refreshId.current);

        onReceiveActivityEnd?.(activity);
      });
    }

    return () => {
      if (activitySubscriber.current) {
        activitySubscriber.current.unsubscribe();
      }
    };
  }, [directLine, onReceiveActivity]);

  return (
    <ConversationThreadInternal
      {...props}
      ref={ref}
      activities={activitiesRef.current}
      onFeedbackButtonClick={sendFeedback}
      conversationId={conversationId}
      renderBottomComponent={renderBottomComponent}
    />
  );
};

export const ConversationThread = memo(forwardRef(ConversationThreadWithForwardRef));
